const DB_NAME = 'InventoryOfflineDB';
const DB_VERSION = 3;  // v3: added pendingCompletionPlots store for offline completion-plot queuing

export const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB open error:', event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // On any upgrade, delete and recreate lookupData so stale data is cleared.
      // The inventories store (pending submissions) is preserved.
      if (db.objectStoreNames.contains('lookupData')) {
        db.deleteObjectStore('lookupData');
      }
      db.createObjectStore('lookupData', { keyPath: 'key' });

      if (!db.objectStoreNames.contains('inventories')) {
        db.createObjectStore('inventories', { keyPath: 'id', autoIncrement: true });
      }

      // v3: store for pending completion-plot submissions
      if (!db.objectStoreNames.contains('pendingCompletionPlots')) {
        db.createObjectStore('pendingCompletionPlots', { keyPath: 'id', autoIncrement: true });
      }
    };
  });
};

export const saveLookup = async (key, data) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('lookupData', 'readwrite');
      const store = tx.objectStore('lookupData');
      const request = store.put({ key, data, updatedAt: Date.now() });

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.error(`Failed to save lookup data for key: ${key}`, err);
  }
};

export const getLookup = async (key) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('lookupData', 'readonly');
      const store = tx.objectStore('lookupData');
      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result ? request.result.data : null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.error(`Failed to get lookup data for key: ${key}`, err);
    return null;
  }
};

export const saveOfflineInventory = async (inventoryData) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('inventories', 'readwrite');
    const store = tx.objectStore('inventories');
    const record = {
      ...inventoryData,
      status: 'pending',
      error: null,
      createdAt: Date.now()
    };
    const request = store.add(record);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getPendingInventories = async () => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('inventories', 'readonly');
    const store = tx.objectStore('inventories');
    const request = store.getAll();

    request.onsuccess = () => {
      const results = request.result || [];
      results.sort((a, b) => a.createdAt - b.createdAt);
      resolve(results);
    };
    request.onerror = () => reject(request.error);
  });
};

export const deleteOfflineInventory = async (id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('inventories', 'readwrite');
    const store = tx.objectStore('inventories');
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const updateOfflineInventory = async (id, updates) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('inventories', 'readwrite');
    const store = tx.objectStore('inventories');
    
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const currentItem = getReq.result;
      if (!currentItem) {
        reject(new Error(`Item with id ${id} not found in offline store`));
        return;
      }
      
      const updatedItem = { ...currentItem, ...updates };
      const putReq = store.put(updatedItem);
      
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
};

const API_BASE = process.env.REACT_APP_API_URL;

/**
 * Called once after login to pre-cache every dropdown used in offline forms.
 * Safe to call again later — it simply overwrites existing data with fresh values.
 * @param {string} token - Bearer access token for authenticated endpoints
 */
export const cacheAllDropdowns = async (token) => {
  if (!navigator.onLine) {
    console.log('[Offline Cache] Skipping dropdown cache — device is offline.');
    return;
  }

  console.log('[Offline Cache] Caching all dropdowns after login…');

  const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

  // ── 1. Public lookup endpoints ─────────────────────────────────────────────
  const publicEndpoints = [
    { url: `${API_BASE}/api/provinces/`,      key: 'provinces' },
    { url: `${API_BASE}/api/districts/`,      key: 'allDistricts' },
    { url: `${API_BASE}/api/municipalities/`, key: 'allMunicipalities' },
    { url: `${API_BASE}/api/forestregime/`,   key: 'forestRegimes' },
    { url: `${API_BASE}/api/forestcategory/`, key: 'forestCategories' },
    { url: `${API_BASE}/api/soil_texture/`,   key: 'soilTextures' },
    { url: `${API_BASE}/api/aspects/`,        key: 'aspects' },
    { url: `${API_BASE}/api/species/`,        key: 'species' },
    { url: `${API_BASE}/api/species_type/`,   key: 'speciesTypes' },
    {
      url: `${API_BASE}/api/observations/`,
      key: 'observations',
      transform: (data) => data.map(item => ({
        id: item.id,
        name: item.destruction,
        description: item.description || ''
      }))
    },
  ];

  // ── 2. Fetch all public endpoints in parallel ──────────────────────────────
  const results = await Promise.allSettled(
    publicEndpoints.map(async (e) => {
      try {
        const res = await fetch(e.url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        const data = e.transform ? e.transform(raw) : raw;
        await saveLookup(e.key, data);
        return { key: e.key, count: Array.isArray(data) ? data.length : 1 };
      } catch (err) {
        console.warn(`[Offline Cache] Failed to cache ${e.key}:`, err.message);
        return null;
      }
    })
  );

  // ── 3. Fetch paginated inventory plots (auth required) ────────────────────
  try {
    let allPlots = [];
    let url = `${API_BASE}/api/inventory-plot/`;
    while (url) {
      const res = await fetch(url, { headers: authHeaders });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) { allPlots = data; break; }
      else if (data?.results) { allPlots = [...allPlots, ...data.results]; url = data.next; }
      else break;
    }
    await saveLookup('inventoryPlots', allPlots);
    console.log(`[Offline Cache] Cached ${allPlots.length} inventory plots.`);
  } catch (err) {
    console.warn('[Offline Cache] Failed to cache inventory plots:', err.message);
  }

  // ── 4. Summary ────────────────────────────────────────────────────────────
  const cached = results
    .filter(r => r.status === 'fulfilled' && r.value)
    .map(r => `${r.value.key}(${r.value.count})`);
  console.log('[Offline Cache] Cached after login:', cached.join(', '));
  console.log('[Offline Cache] ✓ All dropdowns ready for offline use.');

  // ── 5. Cache inventory list (plot_no → id mapping) & existing completion records ───
  const authEndpoints = [
    { url: `${API_BASE}/api/inventory/`,                  key: 'inventoryList' },
    { url: `${API_BASE}/api/inventory-completion-plot/`,  key: 'inventoryCompletions' },
  ];
  await Promise.allSettled(
    authEndpoints.map(async (e) => {
      try {
        let allData = [];
        let url = e.url;
        while (url) {
          const res = await fetch(url, { headers: authHeaders });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (Array.isArray(data)) { allData = data; break; }
          else if (data?.results) { allData = [...allData, ...data.results]; url = data.next || null; }
          else break;
        }
        await saveLookup(e.key, allData);
        console.log(`[Offline Cache] Cached ${allData.length} records for ${e.key}.`);
      } catch (err) {
        console.warn(`[Offline Cache] Failed to cache ${e.key}:`, err.message);
      }
    })
  );
};

// ── Pending Completion Plot helpers ───────────────────────────────────────────

export const saveOfflineCompletionPlot = async (data) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingCompletionPlots', 'readwrite');
    const store = tx.objectStore('pendingCompletionPlots');
    const record = { ...data, status: 'pending', error: null, createdAt: Date.now() };
    const request = store.add(record);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getPendingCompletionPlots = async () => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingCompletionPlots', 'readonly');
    const store = tx.objectStore('pendingCompletionPlots');
    const request = store.getAll();
    request.onsuccess = () => {
      const results = request.result || [];
      results.sort((a, b) => a.createdAt - b.createdAt);
      resolve(results);
    };
    request.onerror = () => reject(request.error);
  });
};

export const deleteOfflineCompletionPlot = async (id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingCompletionPlots', 'readwrite');
    const store = tx.objectStore('pendingCompletionPlots');
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const updateOfflineCompletionPlot = async (id, updates) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingCompletionPlots', 'readwrite');
    const store = tx.objectStore('pendingCompletionPlots');
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const current = getReq.result;
      if (!current) { reject(new Error(`Item ${id} not found in pendingCompletionPlots`)); return; }
      const putReq = store.put({ ...current, ...updates });
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
};
